import { formatShort } from '@/utils/datetime';
/* Sensbey Evidence Router — 設定 UI（AI学習ソース内）
   カタログ / プライバシー / 用語辞書 / テストコンソール / 監査 / フラグ */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, BookOpen, FlaskConical, ScrollText, ToggleLeft,
  FolderTree, Play, RefreshCw, Trash2, Plus,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmtDT = (iso) => (iso ? formatShort(iso) : '—');

const TABS = [
  { id: 'catalogue', label: 'ソースカタログ', icon: FolderTree },
  { id: 'privacy', label: 'プライバシー', icon: ShieldCheck },
  { id: 'terms', label: '用語辞書', icon: BookOpen },
  { id: 'test', label: 'テストコンソール', icon: FlaskConical },
  { id: 'audit', label: '検索監査', icon: ScrollText },
  { id: 'flags', label: '機能フラグ', icon: ToggleLeft },
];

const AUTHORITY_JA = {
  authoritative_internal: '承認済み社内資料',
  authoritative_or_mixed_internal: '社内資料（混在）',
  operational_internal: '業務レコード',
  reference_internal: '参考資料',
  uncontrolled_internal: '未管理',
};

/* 長い一覧は折りたたむ（ページ全体のスクロールを妨げないため、
   開いたときも内側でスクロールする） */
function LongSection({ title, count, unitJa = '件', threshold = 20, action,
                      children, testId, hint }) {
  const [open, setOpen] = useState(true);
  const inited = useRef(false);
  useEffect(() => {
    if (inited.current || !count) return;   // データ到着後に1回だけ判定する
    inited.current = true;
    if (count > threshold) setOpen(false);
  }, [count, threshold]);
  return (
    <div className="rounded-lg border border-zinc-200" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className="inline-flex min-h-[32px] items-center gap-1.5 text-[12.5px] font-medium text-zinc-800"
          data-testid={testId ? `${testId}-toggle` : undefined}>
          <span className="text-zinc-400">{open ? '▾' : '▸'}</span>
          {title}
          <span className="font-normal text-zinc-500">{count ?? 0} {unitJa}</span>
        </button>
        {hint && <span className="text-[11px] text-zinc-400">{hint}</span>}
        <span className="ml-auto flex flex-wrap items-center gap-2">{action}</span>
      </div>
      {open && (
        <div className="max-h-[440px] overflow-auto border-t border-zinc-100"
          data-testid={testId ? `${testId}-body` : undefined}>
          {children}
        </div>
      )}
      {!open && (
        <p className="border-t border-zinc-100 px-3 py-2 text-[11px] text-zinc-500"
          data-testid={testId ? `${testId}-collapsed-note` : undefined}>
          一覧は折りたたんでいます。「{title}」を押すと表示します。
        </p>
      )}
    </div>
  );
}

function CatalogueTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [limit, setLimit] = useState(50);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/evidence/catalogue`, { headers: authHeader() });
      setData(r.data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startCatalogue = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/crawler/nas/catalogue/start`, {}, { headers: authHeader() });
      toast.success('構造カタログの作成を開始しました（ファイル内容は開きません）');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'カタログ作成を開始できませんでした'));
    } finally { setBusy(false); }
  };
  const purge = async () => {
    if (!window.confirm('現在のプライバシーポリシーで除外されるNASデータを検索インデックスから削除します。よろしいですか？')) return;
    try {
      const r = await axios.post(`${API}/api/crawler/nas/purge-excluded`, {}, { headers: authHeader() });
      toast.success(`確認 ${r.data.checked} 件 / 削除 ${r.data.removed} 件`);
      load();
    } catch { toast.error('削除に失敗しました'); }
  };

  const all = data?.collections || [];
  const q = filter.trim().toLowerCase();
  const rows = q
    ? all.filter((r) => `${r.original_path || ''} ${r.description_ja || ''}`.toLowerCase().includes(q))
    : all;
  const shown = rows.slice(0, limit);
  return (
    <div className="space-y-3" data-testid="evr-catalogue-tab">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={startCatalogue} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-700 text-white text-[12px] font-medium hover:bg-teal-800 disabled:opacity-50"
          data-testid="evr-catalogue-start-btn">
          <Play size={13} /> 構造カタログを作成（パス1・内容は開かない）
        </button>
        <button onClick={purge}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 text-[12px] hover:bg-rose-50"
          data-testid="evr-purge-excluded-btn">
          <Trash2 size={13} /> 除外データをインデックスから削除
        </button>
        <button onClick={load} aria-label="一覧を再読み込み" title="一覧を再読み込み"
          className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none"
          data-testid="evr-catalogue-reload">
          <RefreshCw size={13} />
        </button>
        <span className="text-[11px] text-zinc-500 ml-auto">
          {all.length} フォルダー（うち除外 {data?.excluded ?? 0}）
        </span>
      </div>
      <LongSection title="フォルダー一覧" count={rows.length} unitJa="フォルダー"
        testId="evr-catalogue-list" hint="長い一覧は折りたたまれます"
        action={(
          <input value={filter} onChange={(e) => { setFilter(e.target.value); setLimit(50); }}
            placeholder="パス・説明でしぼり込む" aria-label="フォルダーをしぼり込む"
            className="h-9 w-48 rounded-md border border-zinc-200 px-2 text-[12px]"
            data-testid="evr-catalogue-filter" />
        )}>
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-zinc-50 text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">元のパス（原本・変更されません）</th>
              <th className="text-left px-3 py-2 font-medium">説明</th>
              <th className="text-left px-3 py-2 font-medium">信頼度区分</th>
              <th className="text-left px-3 py-2 font-medium">状態</th>
              <th className="text-left px-3 py-2 font-medium">カタログ更新</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-400" data-testid="evr-catalogue-empty">
                {all.length === 0
                  ? 'まだカタログがありません。「構造カタログを作成」を実行してください（NAS接続が必要です）。'
                  : '条件に合うフォルダーがありません。'}
              </td></tr>
            )}
            {shown.map((r) => (
              <tr key={r.source_id} className="border-t border-zinc-100" data-testid={`evr-cat-row-${r.source_id}`}>
                <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-800">{r.original_path || '/'}</td>
                <td className="px-3 py-1.5 text-zinc-600">{r.description_ja || '—'}</td>
                <td className="px-3 py-1.5">{AUTHORITY_JA[r.authority_class] || r.authority_class}</td>
                <td className="px-3 py-1.5">
                  {r.excluded ? (
                    <span className="inline-flex px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 text-[11px]">
                      除外{r.personal_folder ? '（個人）' : ''}
                    </span>
                  ) : (
                    <span className="inline-flex px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px]">
                      検索可（{r.file_count ?? '—'} 件）
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-zinc-500">{fmtDT(r.last_catalogued_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > shown.length && (
          <div className="px-3 py-2 text-center">
            <button type="button" onClick={() => setLimit((n) => n + 100)}
              className="inline-flex min-h-[32px] items-center rounded-md border border-zinc-200 px-3 text-[12px] text-zinc-700 hover:bg-zinc-50"
              data-testid="evr-catalogue-more">
              さらに表示（残り {rows.length - shown.length} フォルダー）
            </button>
          </div>
        )}
      </LongSection>
    </div>
  );
}

function ListEditor({ label, values, onChange, testId, placeholder }) {
  const [input, setInput] = useState('');
  return (
    <div className="space-y-1.5">
      <div className="text-[12px] font-medium text-zinc-700">{label}</div>
      <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
        {(values || []).map((v) => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-100 text-[11px] text-zinc-700">
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))}
              className="text-zinc-400 hover:text-rose-600" data-testid={`${testId}-remove-${v}`}>×</button>
          </span>
        ))}
        {(values || []).length === 0 && <span className="text-[11px] text-zinc-400">なし</span>}
      </div>
      <div className="flex gap-1.5">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={placeholder}
          className="flex-1 px-2 py-1 rounded border border-zinc-200 text-[12px]" data-testid={`${testId}-input`} />
        <button onClick={() => { if (input.trim()) { onChange([...(values || []), input.trim()]); setInput(''); } }}
          className="px-2 py-1 rounded border border-zinc-200 text-[12px] text-zinc-600 hover:bg-zinc-50" data-testid={`${testId}-add`}>
          <Plus size={12} />
        </button>
      </div>
    </div>
  );
}

function ChipGroup({ label, values, testId }) {
  const [open, setOpen] = useState(false);
  const list = values || [];
  const shown = open ? list : list.slice(0, 12);
  return (
    <div>
      <div className="text-[12px] font-medium text-rose-800 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1" data-testid={testId}>
        {shown.map((k) => (
          <span key={k} className="px-1.5 py-0.5 rounded bg-white border border-rose-200 text-[11px] text-rose-700">{k}</span>
        ))}
        {list.length > 12 && (
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
            className="min-h-[28px] rounded border border-rose-200 bg-white px-2 text-[11px] text-rose-700"
            data-testid={testId ? `${testId}-toggle` : undefined}>
            {open ? '折りたたむ' : `+${list.length - 12} 件を表示`}
          </button>
        )}
      </div>
    </div>
  );
}

function PrivacyTab() {
  const [policy, setPolicy] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/evidence/privacy`, { headers: authHeader() });
      setPolicy(r.data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (updates) => {
    try {
      const r = await axios.put(`${API}/api/evidence/privacy`, updates, { headers: authHeader() });
      setPolicy(r.data);
      toast.success('プライバシーポリシーを保存しました');
    } catch { toast.error('保存に失敗しました'); }
  };
  if (!policy) return <div className="text-[12px] text-zinc-400 py-4">読み込み中…</div>;
  return (
    <div className="space-y-4" data-testid="evr-privacy-tab">
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3 space-y-2">
        <ChipGroup label="システム除外（削除できません）"
          values={policy.system_keywords} testId="evr-priv-system-keywords" />
        <ChipGroup label="個人フォルダーの既定パターン（削除できません）"
          values={policy.system_personal_patterns} testId="evr-priv-system-patterns" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <ListEditor label="管理者の個人フォルダーパターン（追加）" values={policy.admin_personal_patterns}
          onChange={(v) => save({ personal_patterns: v })} testId="evr-priv-personal" placeholder="例: 私物メモ" />
        <ListEditor label="除外パス（配下すべて対象外）" values={policy.excluded_paths}
          onChange={(v) => save({ excluded_paths: v })} testId="evr-priv-excluded" placeholder="例: 総務/契約書" />
        <ListEditor label="保護ルート（常に対象外）" values={policy.protected_roots}
          onChange={(v) => save({ protected_roots: v })} testId="evr-priv-protected" placeholder="例: backup" />
        <ListEditor label="承認済みの例外（管理者除外のみ解除・システム除外には無効）" values={policy.exceptions}
          onChange={(v) => save({ exceptions: v })} testId="evr-priv-exceptions" placeholder="例: 総務/契約書/公開資料" />
      </div>
      <DeptRestrictionEditor rules={policy.department_restrictions}
        onChange={(v) => save({ department_restrictions: v })} />
      <PathChecker />
    </div>
  );
}

function DeptRestrictionEditor({ rules, onChange }) {
  const [path, setPath] = useState('');
  const [depts, setDepts] = useState('');
  const list = rules || [];
  const add = () => {
    const d = depts.split(/[,、]/).map((s) => s.trim()).filter(Boolean);
    if (!path.trim() || d.length === 0) { toast.error('パスと部署を入力してください'); return; }
    onChange([...list, { path: path.trim(), departments: d }]);
    setPath(''); setDepts('');
  };
  return (
    <div className="rounded-lg border border-zinc-200 p-3 space-y-2" data-testid="evr-priv-dept">
      <div className="text-[12px] font-medium text-zinc-700">部署制限（指定した部署のみ検索・引用できます）</div>
      <div className="space-y-1">
        {list.length === 0 && <div className="text-[11px] text-zinc-400">なし</div>}
        {list.map((r, i) => (
          <div key={`${r.path}-${i}`} className="flex items-center gap-2 text-[11px]" data-testid={`evr-priv-dept-row-${i}`}>
            <span className="font-mono text-zinc-800">{r.path}</span>
            <span className="text-zinc-400">→</span>
            <span className="text-zinc-600">{(r.departments || []).join('・')}</span>
            <button onClick={() => onChange(list.filter((_, j) => j !== i))}
              className="text-zinc-400 hover:text-rose-600" data-testid={`evr-priv-dept-remove-${i}`}>×</button>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-1.5">
        <input value={path} onChange={(e) => setPath(e.target.value)} placeholder="例: 総務/給与以外の人事資料"
          className="flex-1 px-2 py-1 rounded border border-zinc-200 text-[12px]" data-testid="evr-priv-dept-path" />
        <input value={depts} onChange={(e) => setDepts(e.target.value)} placeholder="許可する部署（カンマ区切り）例: 技術部, 製造部"
          className="flex-1 px-2 py-1 rounded border border-zinc-200 text-[12px]" data-testid="evr-priv-dept-depts" />
        <button onClick={add} aria-label="部署を追加" title="部署を追加"
          className="inline-flex items-center justify-center h-9 w-9 rounded border border-zinc-200 text-[12px] text-zinc-600 hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none"
          data-testid="evr-priv-dept-add"><Plus size={12} /></button>
      </div>
      <div className="text-[11px] text-zinc-500">管理者は制限されません。部署は社員名簿（メールアドレス一致）から判定します。</div>
    </div>
  );
}

function PathChecker() {
  const [path, setPath] = useState('');
  const [result, setResult] = useState(null);
  const check = async () => {
    try {
      const r = await axios.post(`${API}/api/evidence/privacy/check`, { path }, { headers: authHeader() });
      setResult(r.data);
    } catch { toast.error('判定に失敗しました'); }
  };
  return (
    <div className="rounded-lg border border-zinc-200 p-3 space-y-2">
      <div className="text-[12px] font-medium text-zinc-700">パス判定テスト</div>
      <div className="flex gap-1.5">
        <input value={path} onChange={(e) => setPath(e.target.value)} placeholder="例: 技術部/個人/山田"
          className="flex-1 px-2 py-1 rounded border border-zinc-200 text-[12px]" data-testid="evr-pathcheck-input" />
        <button onClick={check} className="px-3 py-1 rounded bg-zinc-800 text-white text-[12px]" data-testid="evr-pathcheck-btn">判定</button>
      </div>
      {result && (
        <div className={`text-[12px] ${result.allowed ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="evr-pathcheck-result">
          {result.allowed ? '✓ クロール・検索の対象になります' : `✕ 対象外 — ${result.reason}`}
        </div>
      )}
    </div>
  );
}

function TermsTab() {
  const [terms, setTerms] = useState([]);
  const [draft, setDraft] = useState({ term_ja: '', term_en: '', aliases: '' });
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/evidence/terminology`, { headers: authHeader() });
      setTerms(r.data.terms || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!draft.term_ja && !draft.term_en) { toast.error('用語（日本語または英語）は必須です'); return; }
    try {
      await axios.post(`${API}/api/evidence/terminology`, {
        term_ja: draft.term_ja, term_en: draft.term_en,
        aliases: draft.aliases.split(/[,、]/).map((s) => s.trim()).filter(Boolean),
      }, { headers: authHeader() });
      setDraft({ term_ja: '', term_en: '', aliases: '' });
      toast.success('用語を追加しました');
      load();
    } catch { toast.error('追加に失敗しました'); }
  };
  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api/evidence/terminology/${id}`, { headers: authHeader() });
      load();
    } catch { toast.error('削除に失敗しました'); }
  };
  return (
    <div className="space-y-3" data-testid="evr-terms-tab">
      <p className="text-[11px] text-zinc-500">
        別名は検索の補助にのみ使われ、NAS上のファイル名・フォルダー名を書き換えることは絶対にありません。
      </p>
      <div className="flex flex-wrap gap-1.5">
        <input value={draft.term_ja} onChange={(e) => setDraft({ ...draft, term_ja: e.target.value })}
          placeholder="正式用語（日本語）" className="px-2 py-1 rounded border border-zinc-200 text-[12px] w-40" data-testid="evr-term-ja-input" />
        <input value={draft.term_en} onChange={(e) => setDraft({ ...draft, term_en: e.target.value })}
          placeholder="正式用語（英語）" className="px-2 py-1 rounded border border-zinc-200 text-[12px] w-40" data-testid="evr-term-en-input" />
        <input value={draft.aliases} onChange={(e) => setDraft({ ...draft, aliases: e.target.value })}
          placeholder="別名（カンマ区切り）" className="px-2 py-1 rounded border border-zinc-200 text-[12px] flex-1 min-w-[160px]" data-testid="evr-term-alias-input" />
        <button onClick={add} className="px-3 py-1 rounded bg-teal-700 text-white text-[12px]" data-testid="evr-term-add-btn">追加</button>
      </div>
      <LongSection title="用語の一覧" count={terms.length} unitJa="語" threshold={15}
        testId="evr-terms-list">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-zinc-50 text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2 font-medium">日本語</th>
              <th className="text-left px-3 py-2 font-medium">英語</th>
              <th className="text-left px-3 py-2 font-medium">別名</th>
              <th className="text-left px-3 py-2 font-medium">関係</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {terms.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-400"
                data-testid="evr-terms-empty">用語はまだ登録されていません。</td></tr>
            )}
            {terms.map((t) => (
              <tr key={t.id} className="border-t border-zinc-100" data-testid={`evr-term-row-${t.id}`}>
                <td className="px-3 py-1.5">{t.term_ja}</td>
                <td className="px-3 py-1.5">{t.term_en}</td>
                <td className="px-3 py-1.5 text-zinc-500">{(t.aliases || []).join('、')}</td>
                <td className="px-3 py-1.5 text-zinc-500">{t.relation === 'exact' ? '同義' : '近い用語'}</td>
                <td className="px-2 py-1.5 text-right">
                  <button onClick={() => remove(t.id)} className="text-zinc-400 hover:text-rose-600" data-testid={`evr-term-del-${t.id}`}>
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </LongSection>
    </div>
  );
}

function TestConsoleTab() {
  const [q, setQ] = useState('2点検査機の配線図を探して');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/evidence/test`, { question: q }, { headers: authHeader() });
      setRes(r.data);
    } catch { toast.error('テスト実行に失敗しました'); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="evr-test-tab">
      <div className="flex gap-1.5">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          className="flex-1 px-2 py-1.5 rounded border border-zinc-200 text-[12px]" data-testid="evr-test-input" />
        <button onClick={run} disabled={busy || !q.trim()}
          className="px-3 py-1.5 rounded bg-teal-700 text-white text-[12px] disabled:opacity-50" data-testid="evr-test-run-btn">
          {busy ? '実行中…' : 'ルーターを実行'}
        </button>
      </div>
      {res && (
        <div className="space-y-2 text-[12px]" data-testid="evr-test-result">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[['意図', res.intent], ['言語', res.query_language],
              ['リスク', res.risk_level], ['モード', res.retrieval_mode]].map(([k, v]) => (
              <div key={k} className="rounded border border-zinc-200 px-2 py-1.5">
                <div className="text-[10px] text-zinc-400">{k}</div>
                <div className="font-medium text-zinc-800">{v || '—'}</div>
              </div>
            ))}
          </div>
          <div className={`rounded px-3 py-2 ${res.nas_would_search ? 'bg-emerald-50 text-emerald-800' : 'bg-zinc-100 text-zinc-600'}`}
            data-testid="evr-test-nas-verdict">
            NAS検索: {res.nas_would_search
              ? `実行（対象: ${(res.nas_collections || []).map((c) => c.original_path).join(' / ') || '—'}）`
              : `実行しない${res.nas_intent ? '（対象カタログなし・権限なし）' : ''}`}
          </div>
          {(res.skipped_sources || []).map((s, i) => (
            <div key={i} className="text-zinc-500">スキップ: {s.source} — {s.reason}</div>
          ))}
          {(res.notes_ja || []).map((n, i) => <div key={i} className="text-zinc-500">{n}</div>)}
          {(res.entities || []).length > 0 && (
            <div className="text-zinc-600">
              検索語: {(res.entities || []).slice(0, 6).map((e) => `${e.original_value}${(e.search_variants || []).length > 1 ? `（${e.search_variants.slice(0, 3).join('・')}）` : ''}`).join('、 ')}
            </div>
          )}
          {(res.citations || []).length > 0 && (
            <div className="rounded border border-zinc-200 divide-y divide-zinc-100">
              {res.citations.map((c) => (
                <div key={c.n} className="px-3 py-1.5 flex items-center gap-2">
                  <span className="text-zinc-400">[{c.n}]</span>
                  <span className="font-medium text-zinc-800">{c.title}</span>
                  <span className="text-[11px] text-zinc-500 ml-auto">{c.authority_label_ja}</span>
                </div>
              ))}
            </div>
          )}
          {(res.conflicts || []).map((c, i) => (
            <div key={i} className="rounded bg-amber-50 text-amber-800 px-3 py-1.5">⚠ {c.message_ja}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditTab() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/evidence/audits?limit=30`, { headers: authHeader() });
      setRows(r.data.audits || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-2" data-testid="evr-audit-tab">
      <button onClick={load} className="inline-flex items-center gap-1 text-[12px] text-zinc-500 hover:text-zinc-800" data-testid="evr-audit-reload">
        <RefreshCw size={12} /> 再読込
      </button>
      <LongSection title="監査記録" count={rows.length} unitJa="件" threshold={10}
        testId="evr-audit-list">
        <div className="divide-y divide-zinc-100">
          {rows.length === 0 && <div className="px-3 py-4 text-[12px] text-zinc-400">監査記録はまだありません。</div>}
          {rows.map((a) => (
            <div key={a.id} className="px-3 py-2 text-[12px]" data-testid={`evr-audit-row-${a.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-zinc-400">{fmtDT(a.started_at)}</span>
                <span className="font-medium text-zinc-800 truncate max-w-[320px]">{a.question}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${a.nas_searched ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                  NAS {a.nas_searched ? '検索' : '未検索'}
                </span>
                <span className="text-zinc-400 ml-auto">{a.retrieval_mode} · {a.latency_ms}ms</span>
              </div>
              {a.nas_skip_reason && <div className="text-[11px] text-zinc-500 mt-0.5">理由: {a.nas_skip_reason}</div>}
              {(a.conflicts || []).length > 0 && (
                <div className="text-[11px] text-amber-700 mt-0.5">⚠ 矛盾 {(a.conflicts || []).length} 件</div>
              )}
            </div>
          ))}
        </div>
      </LongSection>
    </div>
  );
}

function FlagsTab() {
  const [flags, setFlags] = useState(null);
  const [labels, setLabels] = useState({});
  const [labelsEn, setLabelsEn] = useState({});
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/evidence/flags`, { headers: authHeader() });
      setFlags(r.data.flags); setLabels(r.data.labels_ja || {});
      setLabelsEn(r.data.labels_en || {});
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (key) => {
    try {
      const r = await axios.put(`${API}/api/evidence/flags`,
        { updates: { [key]: !flags[key] } }, { headers: authHeader() });
      setFlags(r.data.flags);
    } catch { toast.error('変更に失敗しました'); }
  };
  if (!flags) return <div className="text-[12px] text-zinc-400 py-4">読み込み中…</div>;
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="evr-flags-tab">
      {Object.keys(flags).map((k) => (
        <button key={k} onClick={() => toggle(k)}
          className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-left text-[12px] ${
            flags[k] ? 'border-teal-200 bg-teal-50/50' : 'border-zinc-200 bg-white'}`}
          data-testid={`evr-flag-${k}`}>
          <span className="min-w-0">
            <span className="block text-zinc-700">{labels[k] || k}</span>
            {labelsEn[k] && (
              <span className="block text-[10.5px] text-zinc-500">{labelsEn[k]}</span>
            )}
          </span>
          <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium ${
            flags[k] ? 'bg-teal-700 text-white' : 'bg-zinc-200 text-zinc-600'}`}
            data-testid={`evr-flag-state-${k}`}>
            {flags[k] ? 'ON' : 'OFF'}
          </span>
        </button>
      ))}
    </div>
  );
}

export function EvidenceRouterCard() {
  const [tab, setTab] = useState('catalogue');
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="evidence-router-card">
      <div className="px-5 pt-4 pb-3 border-b border-zinc-100">
        <h2 className="font-bold text-zinc-900 text-[15px]">エビデンスルーター（検索ガバナー）</h2>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          質問ごとに最適な情報源を選び、NASは関連する承認済みフォルダーだけを最小範囲で検索します。
          個人フォルダーは既定で除外され、すべての検索判断が監査ログに記録されます。
        </p>
        <div className="flex flex-wrap gap-1 mt-3">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium ${
                tab === t.id ? 'bg-teal-700 text-white' : 'text-zinc-600 hover:bg-zinc-100'}`}
              data-testid={`evr-tab-${t.id}`}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-5">
        {tab === 'catalogue' && <CatalogueTab />}
        {tab === 'privacy' && <PrivacyTab />}
        {tab === 'terms' && <TermsTab />}
        {tab === 'test' && <TestConsoleTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'flags' && <FlagsTab />}
      </div>
    </div>
  );
}
